import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/lib/api";
import Marquee from "react-fast-marquee";
import { LangPill as SharedLangPill } from "@/components/LangPill";
import RankAnimation from "@/components/landing/RankAnimation";
import HeroDemoRow from "@/components/landing/HeroDemoRow";
import {
  Sparkles,
  ArrowRight,
  ArrowUpRight,
  QrCode,
  ScanText,
  Palette,
  ShieldCheck,
  MailCheck,
  BarChart3,
  FerrisWheel,
  Ticket,
  Cherry,
  Layers,
  Gift,
  Dices,
  Tag,
  Sword,
  Cake,
  Bird,
  Trophy,
  PartyPopper,
  Star,
  Check,
  Infinity as InfinityIcon,
  Menu,
  X,
  Smartphone,
  ClipboardList,
  MousePointerClick,
  Gamepad2,
  Facebook,
  Instagram,
  Youtube,
  Linkedin,
  MapPin,
  Download,
  Mail,
  UserPlus,
  Camera,
  Send,
  Wand2,
  PackageCheck,
  Play,
  CalendarDays,
  ListChecks,
} from "lucide-react";

/* ---------------------------------------------------------------- */
/* Design tokens                                                     */
/* ---------------------------------------------------------------- */
const H = { fontFamily: "Outfit, sans-serif" };
const BODY = { fontFamily: "Manrope, sans-serif" };
const GRAD = "linear-gradient(135deg, #8b5cf6 0%, #3b82f6 50%, #ec4899 100%)";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};
const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.06 } },
};

/* ---------------------------------------------------------------- */
/* Data (labels resolved via i18n `lp.*`)                            */
/* ---------------------------------------------------------------- */
const GAMES = [
  { k: "roue", Icon: FerrisWheel, hue: "#8b5cf6", slug: "demo-roue" },
  { k: "gratter", Icon: Ticket, hue: "#ec4899", slug: "demo-gratter" },
  { k: "slot", Icon: Cherry, hue: "#f59e0b", slug: "demo-slot" },
  { k: "cartes", Icon: Layers, hue: "#3b82f6", slug: "demo-cartes" },
  { k: "mystere", Icon: Gift, hue: "#8b5cf6", slug: "demo-mystere" },
  { k: "tirage", Icon: Dices, hue: "#22c55e", slug: "demo-tirage" },
  { k: "prix", Icon: Tag, hue: "#ec4899", slug: "demo-prix" },
  { k: "ninja", Icon: Sword, hue: "#ef4444", slug: "demo-ninja" },
  { k: "bougies", Icon: Cake, hue: "#f59e0b", slug: "demo-bougies" },
  { k: "flappy", Icon: Bird, hue: "#22d3ee", slug: "demo-flappy" },
  { k: "pronos", Icon: Trophy, hue: "#3b82f6", slug: "demo-pronos" },
  { k: "cadeau", Icon: PartyPopper, hue: "#8b5cf6", slug: "demo-cadeau" },
  { k: "avent", Icon: CalendarDays, hue: "#ef4444", slug: "demo-avent" },
  { k: "quiz", Icon: ListChecks, hue: "#0f766e", slug: "demo-quiz" },
];

const STEP_ICONS = [Smartphone, ClipboardList, MousePointerClick, Gamepad2];
const PRICE_BENEFIT_KEYS = ["price_b1", "price_b2", "price_b3", "price_b4"];

/* ---------------------------------------------------------------- */
/* Language switch — FR | EN (shared component, landing styling)     */
/* ---------------------------------------------------------------- */
function LangPill({ className = "" }) {
  return <SharedLangPill className={`border-black/10 bg-white/80 ${className}`} idPrefix="landing-lang" />;
}

/* ---------------------------------------------------------------- */
/* Nav                                                               */
/* ---------------------------------------------------------------- */
function Nav({ navigate }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const links = [
    { href: "#jeux", label: t("lp.nav_games") },
    { href: "#avantages", label: t("lp.nav_benefits") },
    { href: "#tarifs", label: t("lp.nav_pricing") },
    { href: "#contact", label: t("lp.nav_contact") },
  ];
  return (
    <header className="fixed top-0 inset-x-0 z-50">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 pt-4">
        <nav
          className="flex items-center justify-between rounded-2xl border border-black/5 bg-white/70 backdrop-blur-xl shadow-[0_8px_30px_rgba(0,0,0,0.06)] px-4 sm:px-6 h-14"
          data-testid="landing-nav"
        >
          <Link to="/" className="flex items-center gap-2" data-testid="logo-home-link">
            <span style={H} className="text-lg font-extrabold tracking-[0.14em] text-zinc-900">
              QRISE<span className="qrise-text-gradient">.</span>
            </span>
          </Link>
          <div className="hidden md:flex items-center gap-8">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                className="text-[13.5px] font-semibold text-zinc-500 hover:text-zinc-900 transition-colors"
                data-testid={`nav-link-${l.href.slice(1)}`}
              >
                {l.label}
              </a>
            ))}
          </div>
          <div className="hidden md:flex items-center gap-3">
            <LangPill />
            <button
              onClick={() => navigate("/login")}
              className="text-[13.5px] font-semibold text-zinc-600 hover:text-zinc-900 transition-colors"
              data-testid="nav-login-btn"
            >
              {t("lp.nav_login")}
            </button>
            <button
              onClick={() => {
                document.getElementById("contact")?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              className="h-9 px-4 rounded-full text-[13px] font-bold text-white transition-transform hover:scale-[1.03]"
              style={{ background: GRAD }}
              data-testid="nav-cta-btn"
            >
              {t("lp.nav_cta")}
            </button>
          </div>
          <div className="md:hidden flex items-center gap-2">
            <LangPill />
            <button
              className="text-zinc-700"
              onClick={() => setOpen(!open)}
              aria-label="menu"
              data-testid="nav-mobile-toggle"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </nav>
        {open && (
          <div className="md:hidden mt-2 rounded-2xl border border-black/5 bg-white/95 backdrop-blur-xl shadow-lg p-4 space-y-2">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="block py-2 text-sm font-semibold text-zinc-700"
              >
                {l.label}
              </a>
            ))}
            <button
              onClick={() => {
                setOpen(false);
                document.getElementById("contact")?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              className="w-full h-10 rounded-full text-sm font-bold text-white"
              style={{ background: GRAD }}
              data-testid="nav-mobile-cta"
            >
              {t("lp.nav_cta")}
            </button>
          </div>
        )}
      </div>
    </header>
  );
}

/* ---------------------------------------------------------------- */
/* Hero                                                              */
/* ---------------------------------------------------------------- */
function Hero({ navigate }) {
  const { t } = useTranslation();
  return (
    <section className="relative overflow-hidden pt-40 pb-28 sm:pt-48 sm:pb-36 lg:pt-36 lg:pb-28">
      <div
        className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 h-[520px] w-[820px] rounded-full blur-3xl opacity-25"
        style={{ background: GRAD }}
      />
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage: "radial-gradient(rgba(0,0,0,0.05) 1px, transparent 1px)",
          backgroundSize: "26px 26px",
          maskImage: "radial-gradient(ellipse 70% 60% at 50% 30%, black 40%, transparent 100%)",
          WebkitMaskImage: "radial-gradient(ellipse 70% 60% at 50% 30%, black 40%, transparent 100%)",
        }}
      />
      <div className="relative mx-auto grid max-w-7xl items-center gap-16 px-6 lg:grid-cols-[1.08fr_1fr] lg:gap-12">
        <motion.div variants={stagger} initial="hidden" animate="show" className="text-center lg:text-left">
          <motion.div variants={fadeUp} className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white/80 backdrop-blur px-4 py-1.5 text-[12px] font-bold text-zinc-700 shadow-sm">
            <Sparkles className="h-3.5 w-3.5 text-violet-500" />
            {t("lp.hero_badge")}
          </motion.div>
          <motion.h1
            variants={fadeUp}
            style={H}
            className="mt-8 text-4xl sm:text-5xl xl:text-6xl font-bold tracking-tight text-zinc-900 leading-[1.06]"
            data-testid="hero-title"
          >
            {t("lp.hero_title_1")}{" "}
            <br className="lg:hidden" />
            {t("lp.hero_title_2")} <span className="qrise-text-gradient">{t("lp.hero_title_grad")}</span>.
          </motion.h1>
          <motion.p
            variants={fadeUp}
            style={BODY}
            className="mx-auto lg:mx-0 mt-7 max-w-2xl text-base sm:text-lg leading-relaxed text-zinc-500"
            data-testid="hero-subtitle"
          >
            {t("lp.hero_sub_a")}
            <b className="text-zinc-800">{t("lp.hero_sub_bold")}</b>
            {t("lp.hero_sub_b")}
          </motion.p>
          <motion.div variants={fadeUp} className="mt-10 flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4">
            <button
              onClick={() => navigate("/login")}
              className="group inline-flex items-center gap-2 h-13 px-8 py-3.5 rounded-full text-[15px] font-bold text-white shadow-[0_12px_32px_rgba(139,92,246,0.35)] transition-transform hover:scale-[1.03]"
              style={{ background: GRAD, ...H }}
              data-testid="hero-cta-primary"
            >
              {t("lp.hero_cta_primary")}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
            <a
              href="#avantages"
              className="inline-flex items-center gap-2 h-13 px-8 py-3.5 rounded-full text-[15px] font-bold text-zinc-800 border border-black/10 bg-white/80 backdrop-blur hover:border-black/25 transition-colors"
              style={H}
              data-testid="hero-cta-secondary"
            >
              {t("lp.hero_cta_secondary")}
            </a>
          </motion.div>
          <motion.div
            variants={fadeUp}
            className="mt-14 flex flex-wrap items-center justify-center lg:justify-start gap-x-10 gap-y-3 text-[13px] font-semibold text-zinc-500"
          >
            <span className="inline-flex items-center gap-2">
              <Gamepad2 className="h-4 w-4 text-violet-500" /> {t("lp.hero_proof_games")}
            </span>
            <span className="inline-flex items-center gap-2">
              <InfinityIcon className="h-4 w-4 text-blue-500" /> {t("lp.hero_proof_unlimited")}
            </span>
            <span className="inline-flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-pink-500" /> {t("lp.hero_proof_fraud")}
            </span>
          </motion.div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 28, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="relative mx-auto w-full max-w-[440px]"
        >
          <RankAnimation />
          <HeroDemoRow games={GAMES} />
        </motion.div>
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------- */
/* Trust band — merchant logos                                       */
/* ---------------------------------------------------------------- */
const TRUST_LOGOS = [
  { k: "kleeky", name: "Kleeky — propulsé par Ravate" },
  { k: "conforama", name: "Conforama Réunion" },
  { k: "intersport", name: "Intersport" },
  { k: "mcdonalds", name: "McDonald's" },
  { k: "ravate", name: "Ravate.com" },
  { k: "weldom", name: "Weldom La Réunion" },
  { k: "plein-ciel", name: "Plein Ciel" },
  { k: "naterial", name: "Naterial" },
  { k: "vivre-en-bois", name: "Vivre en Bois" },
  { k: "espace-deco", name: "Espace Déco" },
  { k: "fabrique-de-styles", name: "Fabrique de Styles" },
  { k: "sofa-literie", name: "Sofa & Literie" },
  { k: "dcasa", name: "DCASA" },
  { k: "reve-dailleurs", name: "Rêve d'Ailleurs" },
  { k: "beaute-black", name: "Beauté Black" },
  { k: "centre-auto", name: "Centre Auto" },
  { k: "zotpro", name: "Zotpro" },
  { k: "securycars-tuning", name: "SecuryCars Tuning" },
  { k: "eclat", name: "Éclat" },
];

function TrustBand() {
  const { t } = useTranslation();
  return (
    <section className="py-14 sm:py-16 border-y border-black/[0.05] bg-[#fafafa]" data-testid="trust-band">
      <p
        style={H}
        className="mb-9 text-center text-[12px] font-extrabold uppercase tracking-[0.28em] text-zinc-400"
        data-testid="trust-band-title"
      >
        {t("lp.trust_title")}
      </p>
      <Marquee gradient gradientColor="#fafafa" gradientWidth={120} speed={38} pauseOnHover autoFill>
        {TRUST_LOGOS.map((l) => (
          <div
            key={l.k}
            className="mx-3 flex h-20 w-32 items-center justify-center overflow-hidden rounded-2xl border border-black/[0.06] bg-white p-2.5 shadow-[0_1px_2px_rgba(0,0,0,0.04)]"
            title={l.name}
            data-testid={`trust-logo-${l.k}`}
          >
            <img
              src={`/logos/trust/${l.k}.png`}
              alt={l.name}
              loading="lazy"
              className="max-h-full max-w-full rounded-lg object-contain"
            />
          </div>
        ))}
      </Marquee>
    </section>
  );
}

/* ---------------------------------------------------------------- */
/* Games showcase                                                    */
/* ---------------------------------------------------------------- */
function GamesShowcase() {
  const { t, i18n } = useTranslation();
  const slugSuffix = (i18n.resolvedLanguage || i18n.language || "fr").startsWith("en") ? "-en" : "";
  return (
    <section id="jeux" className="relative py-24 sm:py-32">
      <div className="mx-auto max-w-6xl px-6">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} variants={stagger}>
          <motion.p variants={fadeUp} className="text-[12px] font-extrabold uppercase tracking-[0.28em] qrise-text-gradient" style={H}>
            {t("lp.games_eyebrow")}
          </motion.p>
          <motion.h2 variants={fadeUp} style={H} className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-zinc-900 max-w-xl" data-testid="games-title">
            {t("lp.games_title")}
          </motion.h2>
          <motion.p variants={fadeUp} style={BODY} className="mt-4 max-w-2xl text-zinc-500 leading-relaxed">
            {t("lp.games_sub_a")}
            <b className="text-zinc-800">{t("lp.games_sub_bold")}</b>
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-60px" }}
          variants={stagger}
          className="mt-14 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4"
        >
          {GAMES.map((g) => (
            <motion.a
              key={g.k}
              variants={fadeUp}
              href={`/play/${g.slug}${slugSuffix}`}
              target="_blank"
              rel="noreferrer"
              className="group relative flex flex-col rounded-2xl border border-black/[0.06] bg-white p-5 shadow-[0_1px_2px_rgba(0,0,0,0.04)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_16px_40px_rgba(0,0,0,0.09)]"
              data-testid={`game-card-${g.k}`}
            >
              <div
                className="flex h-11 w-11 items-center justify-center rounded-xl transition-transform duration-300 group-hover:scale-110"
                style={{ background: `${g.hue}14`, color: g.hue }}
              >
                <g.Icon style={{ width: 22, height: 22 }} />
              </div>
              <div style={H} className="mt-4 text-[14.5px] font-bold text-zinc-900 leading-snug">
                {t(`lp.game_${g.k}_name`)}
              </div>
              <div style={BODY} className="mt-1 text-[12px] text-zinc-500 leading-snug">
                {t(`lp.game_${g.k}_desc`)}
              </div>
              <div
                style={{ ...H, color: g.hue }}
                className="mt-3 inline-flex items-center gap-1 text-[11.5px] font-extrabold uppercase tracking-wide opacity-70 transition-opacity duration-300 group-hover:opacity-100"
                data-testid={`game-demo-link-${g.k}`}
              >
                <Play className="h-3 w-3" style={{ fill: "currentColor" }} />
                {t("lp.game_try")}
              </div>
            </motion.a>
          ))}
          <motion.a
            variants={fadeUp}
            href="#tarifs"
            className="group relative flex flex-col justify-between rounded-2xl p-5 text-white transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_16px_40px_rgba(139,92,246,0.35)]"
            style={{ background: GRAD }}
            data-testid="game-card-more"
          >
            <Sparkles className="h-6 w-6 opacity-90" />
            <div>
              <div style={H} className="text-[14.5px] font-bold leading-snug">
                {t("lp.game_more_title")}
              </div>
              <div style={BODY} className="mt-1 inline-flex items-center gap-1 text-[12px] font-semibold opacity-90">
                {t("lp.game_more_link")} <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </div>
            </div>
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------- */
/* How it works                                                      */
/* ---------------------------------------------------------------- */
function HowItWorks() {
  const { t } = useTranslation();
  return (
    <section className="py-24 sm:py-28 bg-[#0d1024] relative overflow-hidden">
      <div
        className="pointer-events-none absolute -bottom-40 right-0 h-[420px] w-[620px] rounded-full blur-3xl opacity-20"
        style={{ background: GRAD }}
      />
      <div className="relative mx-auto max-w-6xl px-6">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} variants={stagger}>
          <motion.p variants={fadeUp} className="text-[12px] font-extrabold uppercase tracking-[0.28em] text-pink-400" style={H}>
            {t("lp.how_eyebrow")}
          </motion.p>
          <motion.h2 variants={fadeUp} style={H} className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-white max-w-2xl">
            {t("lp.how_title")}
          </motion.h2>
        </motion.div>
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-60px" }}
          variants={stagger}
          className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5"
        >
          {STEP_ICONS.map((Icon, i) => (
            <motion.div
              key={i}
              variants={fadeUp}
              className="rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl p-7"
              data-testid={`step-card-${i + 1}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl text-white" style={{ background: GRAD }}>
                  <Icon className="h-5 w-5" />
                </div>
                <span style={H} className="text-4xl font-light text-white/15">
                  0{i + 1}
                </span>
              </div>
              <div style={H} className="mt-5 text-[16px] font-bold text-white">
                {t(`lp.step${i + 1}_t`)}
              </div>
              <p style={BODY} className="mt-2 text-[13px] leading-relaxed text-zinc-400">
                {t(`lp.step${i + 1}_d`)}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------- */
/* CTA Showcase — all winning actions                                */
/* ---------------------------------------------------------------- */
function GoogleIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M21.35 11.1h-9.17v2.73h6.51c-.33 3.81-3.5 5.44-6.5 5.44C8.36 19.27 5 16.25 5 12c0-4.1 3.2-7.27 7.2-7.27 3.09 0 4.9 1.97 4.9 1.97L19 4.72S16.56 2 12.1 2C6.42 2 2.03 6.8 2.03 12c0 5.05 4.13 10 10.22 10 5.35 0 9.25-3.67 9.25-9.09 0-1.15-.15-1.81-.15-1.81z" />
    </svg>
  );
}
function TiktokIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12.53.02C13.84 0 15.14.01 16.44 0c.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
    </svg>
  );
}

const CTA_ITEMS = [
  { k: "google_review", Icon: GoogleIcon, hue: "#4285F4" },
  { k: "instagram_follow", Icon: Instagram, hue: "#E4405F" },
  { k: "facebook_like", Icon: Facebook, hue: "#1877F2" },
  { k: "tiktok_follow", Icon: TiktokIcon, hue: "#101010" },
  { k: "youtube_subscribe", Icon: Youtube, hue: "#FF0000" },
  { k: "linkedin_follow", Icon: Linkedin, hue: "#0A66C2" },
  { k: "tripadvisor_review", Icon: MapPin, hue: "#34a87c" },
  { k: "instagram_story", Icon: Camera, hue: "#C13584" },
  { k: "app_download", Icon: Download, hue: "#2563eb" },
  { k: "newsletter_signup", Icon: Mail, hue: "#8b5cf6" },
  { k: "account_creation", Icon: UserPlus, hue: "#ec4899" },
];

function CtaShowcase() {
  const { t } = useTranslation();
  return (
    <section id="actions" className="py-24 sm:py-28" data-testid="cta-showcase-section">
      <div className="mx-auto max-w-6xl px-6">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} variants={stagger}>
          <motion.p variants={fadeUp} className="text-[12px] font-extrabold uppercase tracking-[0.28em] qrise-text-gradient" style={H}>
            {t("lp.ctas_eyebrow")}
          </motion.p>
          <motion.h2 variants={fadeUp} style={H} className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-zinc-900 max-w-2xl" data-testid="cta-showcase-title">
            {t("lp.ctas_title")}
          </motion.h2>
          <motion.p variants={fadeUp} style={BODY} className="mt-4 max-w-2xl text-zinc-500 leading-relaxed">
            {t("lp.ctas_sub")}
          </motion.p>
        </motion.div>
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-60px" }}
          variants={stagger}
          className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
        >
          {CTA_ITEMS.map((c) => (
            <motion.div
              key={c.k}
              variants={fadeUp}
              className="group rounded-2xl border border-black/[0.07] bg-white p-5 shadow-[0_1px_2px_rgba(0,0,0,0.04)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_14px_34px_rgba(0,0,0,0.09)]"
              data-testid={`cta-chip-${c.k}`}
            >
              <div className="flex items-center gap-3">
                <span
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-transform duration-300 group-hover:scale-110"
                  style={{ background: `${c.hue}14`, color: c.hue }}
                >
                  <c.Icon className="h-4 w-4" />
                </span>
                <span style={H} className="text-[14px] font-bold text-zinc-900">{t(`lp.cta_${c.k}_label`)}</span>
              </div>
              <p style={BODY} className="mt-2.5 text-[12.5px] leading-relaxed text-zinc-500">
                {t(`lp.cta_${c.k}_benefit`)}
              </p>
            </motion.div>
          ))}
          <motion.div
            variants={fadeUp}
            className="flex flex-col justify-center rounded-2xl p-5 text-white shadow-[0_8px_22px_rgba(139,92,246,0.30)]"
            style={{ background: GRAD }}
            data-testid="cta-chip-unlimited"
          >
            <div className="flex items-center gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/20">
                <InfinityIcon className="h-4 w-4" />
              </span>
              <span style={H} className="text-[14px] font-bold">{t("lp.cta_unlimited_title")}</span>
            </div>
            <p style={BODY} className="mt-2.5 text-[12.5px] leading-relaxed text-white/85">
              {t("lp.cta_unlimited_desc")}
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------- */
/* Advantages — Bento grid                                           */
/* ---------------------------------------------------------------- */
function BentoCard({ className = "", children, testid }) {
  return (
    <motion.div
      variants={fadeUp}
      className={`relative overflow-hidden rounded-3xl border border-black/[0.06] bg-white p-8 shadow-[0_1px_2px_rgba(0,0,0,0.04)] transition-shadow duration-300 hover:shadow-[0_20px_50px_rgba(0,0,0,0.08)] ${className}`}
      data-testid={testid}
    >
      {children}
    </motion.div>
  );
}

const ADV_SQUARES = [
  { k: "custom", Icon: Palette, cls: "bg-pink-50 text-pink-500" },
  { k: "fraud", Icon: ShieldCheck, cls: "bg-blue-50 text-blue-500", testid: "antifraud" },
  { k: "email", Icon: MailCheck, cls: "bg-violet-50 text-violet-500" },
  { k: "turnkey", Icon: Wand2, cls: "text-white", grad: true },
  { k: "lottery", Icon: Dices, cls: "bg-emerald-50 text-emerald-500" },
  { k: "claims", Icon: PackageCheck, cls: "bg-amber-50 text-amber-500" },
];

function Advantages() {
  const { t } = useTranslation();
  return (
    <section id="avantages" className="py-24 sm:py-32 bg-[#fafafa]">
      <div className="mx-auto max-w-6xl px-6">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} variants={stagger}>
          <motion.p variants={fadeUp} className="text-[12px] font-extrabold uppercase tracking-[0.28em] qrise-text-gradient" style={H}>
            {t("lp.adv_eyebrow")}
          </motion.p>
          <motion.h2 variants={fadeUp} style={H} className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-zinc-900 max-w-2xl" data-testid="advantages-title">
            {t("lp.adv_title")}
          </motion.h2>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-60px" }}
          variants={stagger}
          className="mt-14 grid grid-cols-1 md:grid-cols-12 gap-6"
        >
          <BentoCard className="md:col-span-7 min-h-[300px] flex flex-col justify-between" testid="advantage-card-ocr">
            <div>
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl text-white" style={{ background: GRAD }}>
                <ScanText className="h-6 w-6" />
              </div>
              <h3 style={H} className="mt-6 text-2xl font-bold text-zinc-900">
                {t("lp.adv_ocr_title")}
              </h3>
              <p style={BODY} className="mt-3 max-w-md text-[14px] leading-relaxed text-zinc-500">
                {t("lp.adv_ocr_desc")}
              </p>
            </div>
            <div className="mt-6 flex flex-wrap gap-2">
              {["adv_ocr_chip1", "adv_ocr_chip2", "adv_ocr_chip3"].map((c) => (
                <span key={c} style={BODY} className="rounded-full bg-violet-50 text-violet-600 px-3 py-1 text-[11.5px] font-bold">
                  {t(`lp.${c}`)}
                </span>
              ))}
            </div>
          </BentoCard>

          <BentoCard className="md:col-span-5 min-h-[300px] p-0" testid="advantage-card-qr">
            <img
              src="https://images.unsplash.com/photo-1595079676339-1534801ad6cf?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"
              alt={t("lp.adv_qr_alt")}
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0d1024]/95 via-[#0d1024]/40 to-transparent" />
            <div className="relative flex h-full flex-col justify-end p-8">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/15 backdrop-blur text-white">
                <QrCode className="h-5 w-5" />
              </div>
              <h3 style={H} className="mt-4 text-xl font-bold text-white">
                {t("lp.adv_qr_title")}
              </h3>
              <p style={BODY} className="mt-2 text-[13px] leading-relaxed text-zinc-300">
                {t("lp.adv_qr_desc")}
              </p>
            </div>
          </BentoCard>

          {ADV_SQUARES.map((a) => (
            <BentoCard key={a.k} className="md:col-span-4" testid={`advantage-card-${a.testid || a.k}`}>
              <div
                className={`flex h-11 w-11 items-center justify-center rounded-xl ${a.cls}`}
                style={a.grad ? { background: GRAD } : undefined}
              >
                <a.Icon className="h-5 w-5" />
              </div>
              <h3 style={H} className="mt-5 text-lg font-bold text-zinc-900">
                {t(`lp.adv_${a.k}_title`)}
              </h3>
              <p style={BODY} className="mt-2 text-[13px] leading-relaxed text-zinc-500">
                {t(`lp.adv_${a.k}_desc`)}
              </p>
            </BentoCard>
          ))}

          <BentoCard className="md:col-span-12 md:flex items-center gap-10" testid="advantage-card-analytics">
            <div className="md:flex-1">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-50 text-emerald-500">
                <BarChart3 className="h-5 w-5" />
              </div>
              <h3 style={H} className="mt-5 text-lg font-bold text-zinc-900">
                {t("lp.adv_data_title")}
              </h3>
              <p style={BODY} className="mt-2 max-w-lg text-[13px] leading-relaxed text-zinc-500">
                {t("lp.adv_data_desc")}
              </p>
            </div>
            <div className="mt-8 md:mt-0 grid grid-cols-3 gap-4 md:w-[380px]">
              {[
                ["+5×", "adv_stat1_l"],
                ["68%", "adv_stat2_l"],
                ["1 min", "adv_stat3_l"],
              ].map(([v, l]) => (
                <div key={l} className="rounded-2xl bg-[#0d1024] p-4 text-center">
                  <div style={H} className="text-xl font-extrabold qrise-text-gradient">{v}</div>
                  <div style={BODY} className="mt-1 text-[10.5px] font-semibold text-zinc-400 leading-tight">{t(`lp.${l}`)}</div>
                </div>
              ))}
            </div>
          </BentoCard>
        </motion.div>
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------- */
/* Game options — per-game toggles                                   */
/* ---------------------------------------------------------------- */
function ToggleOn() {
  return (
    <span className="relative inline-flex h-6 w-11 shrink-0 items-center rounded-full" style={{ background: GRAD }} aria-hidden="true">
      <span className="absolute right-0.5 h-5 w-5 rounded-full bg-white shadow-sm" />
    </span>
  );
}

const GAME_OPTIONS = [
  { k: "receipt", Icon: ScanText },
  { k: "address", Icon: MapPin },
  { k: "birthdate", Icon: Cake },
];

function GameOptions() {
  const { t } = useTranslation();
  return (
    <section id="options" className="py-24 sm:py-28" data-testid="game-options-section">
      <div className="mx-auto max-w-6xl px-6 grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-16 items-center">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} variants={stagger}>
          <motion.p variants={fadeUp} className="text-[12px] font-extrabold uppercase tracking-[0.28em] qrise-text-gradient" style={H}>
            {t("lp.opt_eyebrow")}
          </motion.p>
          <motion.h2 variants={fadeUp} style={H} className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-zinc-900" data-testid="game-options-title">
            {t("lp.opt_title")}
          </motion.h2>
          <motion.p variants={fadeUp} style={BODY} className="mt-4 max-w-md text-zinc-500 leading-relaxed">
            {t("lp.opt_sub_a")}
            <b className="text-zinc-800">{t("lp.opt_sub_bold")}</b>
            {t("lp.opt_sub_b")}
          </motion.p>
          <motion.ul variants={fadeUp} className="mt-8 space-y-3">
            {["opt_li1", "opt_li2", "opt_li3"].map((f) => (
              <li key={f} style={BODY} className="flex items-center gap-3 text-[13.5px] font-semibold text-zinc-700">
                <span className="flex h-5 w-5 items-center justify-center rounded-full" style={{ background: GRAD }}>
                  <Check className="h-3 w-3 text-white" />
                </span>
                {t(`lp.${f}`)}
              </li>
            ))}
          </motion.ul>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-60px" }}
          variants={stagger}
          className="rounded-3xl border border-black/[0.06] bg-white p-2 shadow-[0_20px_50px_rgba(0,0,0,0.08)]"
          data-testid="game-options-mockup"
        >
          <div style={H} className="px-6 pt-5 pb-3 text-[11px] font-extrabold uppercase tracking-[0.2em] text-zinc-400">
            {t("lp.opt_mock_title")}
          </div>
          <div className="divide-y divide-black/[0.05]">
            {GAME_OPTIONS.map((o) => (
              <motion.div key={o.k} variants={fadeUp} className="flex items-start gap-4 px-6 py-5" data-testid={`game-option-${o.k}`}>
                <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-violet-50 text-violet-500">
                  <o.Icon style={{ width: 18, height: 18 }} />
                </span>
                <div className="flex-1">
                  <div className="flex items-center justify-between gap-4">
                    <span style={H} className="text-[15px] font-bold text-zinc-900">{t(`lp.opt_${o.k}_title`)}</span>
                    <ToggleOn />
                  </div>
                  <p style={BODY} className="mt-1.5 text-[12.5px] leading-relaxed text-zinc-500">{t(`lp.opt_${o.k}_desc`)}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------- */
/* Benefit CTAs                                                      */
/* ---------------------------------------------------------------- */
const BENEFIT_ITEMS = [
  { k: "google", Icon: Star, hue: "#f59e0b" },
  { k: "sales", Icon: Gift, hue: "#ec4899" },
  { k: "contacts", Icon: MailCheck, hue: "#8b5cf6" },
];

function BenefitCtas({ navigate }) {
  const { t } = useTranslation();
  return (
    <section className="py-24 sm:py-28">
      <div className="mx-auto max-w-6xl px-6">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-60px" }}
          variants={stagger}
          className="grid grid-cols-1 md:grid-cols-3 gap-6"
        >
          {BENEFIT_ITEMS.map((b) => (
            <motion.div
              key={b.k}
              variants={fadeUp}
              className="group flex flex-col rounded-3xl border border-black/[0.06] bg-white p-8 shadow-[0_1px_2px_rgba(0,0,0,0.04)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_50px_rgba(0,0,0,0.09)]"
              data-testid={`benefit-cta-${b.k}`}
            >
              <div className="flex h-11 w-11 items-center justify-center rounded-xl" style={{ background: `${b.hue}14`, color: b.hue }}>
                <b.Icon className="h-5 w-5" />
              </div>
              <h3 style={H} className="mt-5 text-lg font-bold text-zinc-900">{t(`lp.ben_${b.k}_title`)}</h3>
              <p style={BODY} className="mt-2 flex-1 text-[13px] leading-relaxed text-zinc-500">{t(`lp.ben_${b.k}_desc`)}</p>
              <button
                onClick={() => navigate("/login")}
                style={H}
                className="mt-6 inline-flex items-center gap-2 text-[13.5px] font-bold text-zinc-900 group-hover:qrise-text-gradient transition-colors"
                data-testid={`benefit-cta-${b.k}-btn`}
              >
                {t(`lp.ben_${b.k}_cta`)}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </button>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------- */
/* Pricing                                                           */
/* ---------------------------------------------------------------- */
function Pricing({ navigate }) {
  const { t } = useTranslation();
  return (
    <section id="tarifs" className="py-24 sm:py-32 bg-[#0d1024] relative overflow-hidden">
      <div
        className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 h-[420px] w-[720px] rounded-full blur-3xl opacity-20"
        style={{ background: GRAD }}
      />
      <div className="relative mx-auto max-w-5xl px-6">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} variants={stagger} className="text-center">
          <motion.p variants={fadeUp} className="text-[12px] font-extrabold uppercase tracking-[0.28em] text-pink-400" style={H}>
            {t("lp.price_eyebrow")}
          </motion.p>
          <motion.h2 variants={fadeUp} style={H} className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-white" data-testid="pricing-title">
            {t("lp.price_title")}
          </motion.h2>
          <motion.p variants={fadeUp} style={BODY} className="mx-auto mt-4 max-w-xl text-zinc-400 leading-relaxed">
            {t("lp.price_sub")}
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-60px" }}
          variants={stagger}
          className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch"
        >
          <motion.div variants={fadeUp} className="relative rounded-3xl p-[1.5px]" style={{ background: GRAD }} data-testid="pricing-card-engagement">
            <div className="relative h-full rounded-3xl bg-[#12142a] p-9 flex flex-col">
              <span
                className="absolute -top-3.5 left-8 rounded-full px-3.5 py-1 text-[11px] font-extrabold uppercase tracking-wider text-white"
                style={{ background: GRAD, ...H }}
              >
                {t("lp.price_recommended")}
              </span>
              <div style={H} className="text-[15px] font-bold text-white">{t("lp.price_plan1")}</div>
              <div className="mt-4 flex items-baseline gap-2">
                <span style={H} className="text-5xl font-extrabold text-white">149€</span>
                <span style={BODY} className="text-sm font-semibold text-zinc-400">{t("lp.price_unit")}</span>
              </div>
              <p style={BODY} className="mt-3 text-[12.5px] leading-relaxed text-zinc-400">
                {t("lp.price_plan1_desc_a")}
                <b className="text-white">{t("lp.price_plan1_desc_bold")}</b>.
              </p>
              <div className="my-6 h-px bg-white/10" />
              <ul className="space-y-3 flex-1">
                {PRICE_BENEFIT_KEYS.map((b) => (
                  <li key={b} style={BODY} className="flex items-center gap-3 text-[13.5px] font-semibold text-zinc-200">
                    <span className="flex h-5 w-5 items-center justify-center rounded-full" style={{ background: GRAD }}>
                      <Check className="h-3 w-3 text-white" />
                    </span>
                    {t(`lp.${b}`)}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => navigate("/login")}
                className="mt-8 h-12 w-full rounded-full text-[14px] font-bold text-white transition-transform hover:scale-[1.02]"
                style={{ background: GRAD, ...H }}
                data-testid="pricing-cta-engagement"
              >
                {t("lp.price_plan1_cta")}
              </button>
            </div>
          </motion.div>

          <motion.div
            variants={fadeUp}
            className="rounded-3xl border border-white/10 bg-white/[0.04] backdrop-blur-xl p-9 flex flex-col"
            data-testid="pricing-card-oneshot"
          >
            <div style={H} className="text-[15px] font-bold text-white">{t("lp.price_plan2")}</div>
            <div className="mt-4 flex items-baseline gap-2">
              <span style={H} className="text-5xl font-extrabold text-white">299€</span>
              <span style={BODY} className="text-sm font-semibold text-zinc-400">{t("lp.price_unit")}</span>
            </div>
            <p style={BODY} className="mt-3 text-[12.5px] leading-relaxed text-zinc-400">
              {t("lp.price_plan2_desc_a")}
              <b className="text-white">{t("lp.price_plan2_desc_bold")}</b>
              {t("lp.price_plan2_desc_b")}
            </p>
            <div className="my-6 h-px bg-white/10" />
            <ul className="space-y-3 flex-1">
              {PRICE_BENEFIT_KEYS.map((b) => (
                <li key={b} style={BODY} className="flex items-center gap-3 text-[13.5px] font-semibold text-zinc-200">
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white/15">
                    <Check className="h-3 w-3 text-white" />
                  </span>
                  {t(`lp.${b}`)}
                </li>
              ))}
            </ul>
            <button
              onClick={() => navigate("/login")}
              className="mt-8 h-12 w-full rounded-full border border-white/25 text-[14px] font-bold text-white hover:bg-white/10 transition-colors"
              style={H}
              data-testid="pricing-cta-oneshot"
            >
              {t("lp.price_plan2_cta")}
            </button>
          </motion.div>
        </motion.div>

        <motion.p
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          variants={fadeUp}
          style={BODY}
          className="mt-8 text-center text-[12px] text-zinc-500"
        >
          {t("lp.price_footnote")}
        </motion.p>
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------- */
/* Contact                                                           */
/* ---------------------------------------------------------------- */
const inputCls =
  "w-full h-11 rounded-xl border border-black/10 bg-white px-4 text-[14px] text-zinc-900 placeholder:text-zinc-400 outline-none transition-colors focus:border-violet-400 focus:ring-2 focus:ring-violet-100";

function ContactSection() {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function submit(e) {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error(t("lp.contact_err_required"));
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/public/contact`, form);
      setDone(true);
    } catch {
      toast.error(t("lp.contact_err_generic"));
    } finally {
      setSending(false);
    }
  }

  return (
    <section id="contact" className="py-24 sm:py-32 bg-[#fafafa]" data-testid="contact-section">
      <div className="mx-auto max-w-6xl px-6 grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-16 items-start">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} variants={stagger}>
          <motion.p variants={fadeUp} className="text-[12px] font-extrabold uppercase tracking-[0.28em] qrise-text-gradient" style={H}>
            {t("lp.contact_eyebrow")}
          </motion.p>
          <motion.h2 variants={fadeUp} style={H} className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-zinc-900" data-testid="contact-title">
            {t("lp.contact_title")}
          </motion.h2>
          <motion.p variants={fadeUp} style={BODY} className="mt-4 max-w-md text-zinc-500 leading-relaxed">
            {t("lp.contact_sub")}
          </motion.p>
          <motion.ul variants={fadeUp} className="mt-8 space-y-3">
            {["contact_li1", "contact_li2", "contact_li3"].map((f) => (
              <li key={f} style={BODY} className="flex items-center gap-3 text-[13.5px] font-semibold text-zinc-700">
                <span className="flex h-5 w-5 items-center justify-center rounded-full" style={{ background: GRAD }}>
                  <Check className="h-3 w-3 text-white" />
                </span>
                {t(`lp.${f}`)}
              </li>
            ))}
          </motion.ul>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-60px" }}
          variants={fadeUp}
          className="rounded-3xl border border-black/[0.06] bg-white p-8 shadow-[0_20px_50px_rgba(0,0,0,0.08)]"
          data-testid="contact-form-card"
        >
          {done ? (
            <div className="py-14 text-center" data-testid="contact-success">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full" style={{ background: GRAD }}>
                <Check className="h-7 w-7 text-white" />
              </div>
              <h3 style={H} className="mt-5 text-xl font-bold text-zinc-900">{t("lp.contact_success_title")}</h3>
              <p style={BODY} className="mx-auto mt-2 max-w-xs text-[13.5px] text-zinc-500 leading-relaxed">
                {t("lp.contact_success_body", { name: form.name.split(" ")[0], email: form.email })}
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input className={inputCls} style={BODY} placeholder={t("lp.contact_ph_name")} value={form.name} onChange={set("name")} data-testid="contact-input-name" />
                <input className={inputCls} style={BODY} type="email" placeholder={t("lp.contact_ph_email")} value={form.email} onChange={set("email")} data-testid="contact-input-email" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input className={inputCls} style={BODY} placeholder={t("lp.contact_ph_company")} value={form.company} onChange={set("company")} data-testid="contact-input-company" />
                <input className={inputCls} style={BODY} placeholder={t("lp.contact_ph_phone")} value={form.phone} onChange={set("phone")} data-testid="contact-input-phone" />
              </div>
              <textarea
                className={`${inputCls} h-32 resize-none py-3`}
                style={BODY}
                placeholder={t("lp.contact_ph_message")}
                value={form.message}
                onChange={set("message")}
                data-testid="contact-input-message"
              />
              <button
                type="submit"
                disabled={sending}
                className="inline-flex w-full items-center justify-center gap-2 h-12 rounded-full text-[14px] font-bold text-white transition-transform hover:scale-[1.02] disabled:opacity-60"
                style={{ background: GRAD, ...H }}
                data-testid="contact-submit-btn"
              >
                {sending ? t("lp.contact_sending") : t("lp.contact_send")}
                {!sending && <Send className="h-4 w-4" />}
              </button>
              <p style={BODY} className="text-center text-[11.5px] text-zinc-400">
                {t("lp.contact_privacy")}
              </p>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------- */
/* Final CTA + Footer                                                */
/* ---------------------------------------------------------------- */
function FinalCta({ navigate }) {
  const { t } = useTranslation();
  return (
    <section className="py-24 sm:py-32">
      <div className="mx-auto max-w-4xl px-6 text-center">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} variants={stagger}>
          <motion.h2 variants={fadeUp} style={H} className="text-3xl sm:text-5xl font-bold tracking-tight text-zinc-900 leading-tight">
            {t("lp.final_title_1")}
            <br />
            {t("lp.final_title_2")}<span className="qrise-text-gradient">{t("lp.final_title_grad")}</span>.
          </motion.h2>
          <motion.p variants={fadeUp} style={BODY} className="mx-auto mt-6 max-w-xl text-zinc-500 leading-relaxed">
            {t("lp.final_sub")}
          </motion.p>
          <motion.div variants={fadeUp} className="mt-10">
            <button
              onClick={() => navigate("/login")}
              className="group inline-flex items-center gap-2 px-9 py-4 rounded-full text-[15px] font-bold text-white shadow-[0_12px_32px_rgba(139,92,246,0.35)] transition-transform hover:scale-[1.03]"
              style={{ background: GRAD, ...H }}
              data-testid="final-cta-btn"
            >
              {t("lp.final_cta")}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

function Footer() {
  const { t } = useTranslation();
  return (
    <footer className="border-t border-black/[0.06] py-12" data-testid="landing-footer">
      <div className="mx-auto max-w-6xl px-6 flex flex-col sm:flex-row items-center justify-between gap-6">
        <div style={H} className="text-base font-extrabold tracking-[0.14em] text-zinc-900">
          QRISE<span className="qrise-text-gradient">.</span>
        </div>
        <div style={BODY} className="flex flex-wrap items-center justify-center gap-x-8 gap-y-2 text-[12.5px] font-semibold text-zinc-500">
          <a href="#jeux" className="hover:text-zinc-900 transition-colors">{t("lp.nav_games")}</a>
          <a href="#avantages" className="hover:text-zinc-900 transition-colors">{t("lp.nav_benefits")}</a>
          <a href="#tarifs" className="hover:text-zinc-900 transition-colors">{t("lp.nav_pricing")}</a>
          <a href="#contact" className="hover:text-zinc-900 transition-colors" data-testid="footer-contact-link">{t("lp.nav_contact")}</a>
          <Link to="/login" className="hover:text-zinc-900 transition-colors" data-testid="footer-login-link">{t("lp.footer_login")}</Link>
        </div>
        <div style={BODY} className="text-[11.5px] text-zinc-400">
          © {new Date().getFullYear()} {t("lp.footer_copy")}
        </div>
      </div>
      <div style={BODY} className="mx-auto max-w-6xl px-6 mt-6 pt-6 border-t border-black/[0.04] flex flex-wrap items-center justify-center gap-x-8 gap-y-2 text-[11.5px] font-semibold text-zinc-400">
        <Link to="/conditions#mentions-legales" className="hover:text-zinc-700 transition-colors" data-testid="footer-legal-link">{t("lp.footer_legal")}</Link>
        <Link to="/conditions#cgu" className="hover:text-zinc-700 transition-colors" data-testid="footer-cgu-link">{t("lp.footer_terms")}</Link>
        <Link to="/conditions#cgv" className="hover:text-zinc-700 transition-colors" data-testid="footer-cgv-link">{t("lp.footer_cgv")}</Link>
        <Link to="/conditions#confidentialite" className="hover:text-zinc-700 transition-colors" data-testid="footer-privacy-link">{t("lp.footer_privacy")}</Link>
        <span className="text-zinc-300">{t("lp.footer_publisher")}</span>
      </div>
    </footer>
  );
}

/* ---------------------------------------------------------------- */
/* Page                                                              */
/* ---------------------------------------------------------------- */
export default function Landing() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-white text-zinc-900" style={BODY} data-testid="landing-page">
      <Nav navigate={navigate} />
      <Hero navigate={navigate} />
      <TrustBand />
      <GamesShowcase />
      <HowItWorks />
      <CtaShowcase />
      <Advantages />
      <GameOptions />
      <BenefitCtas navigate={navigate} />
      <Pricing navigate={navigate} />
      <ContactSection />
      <FinalCta navigate={navigate} />
      <Footer />
    </div>
  );
}
